import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './SignUp.scss';

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, isLoading, isAuthenticated } = useAuth();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated() && !isLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await signUp(formData.email, formData.password, formData.name);
      
      // Check if email confirmation is needed
      if (result?.needsConfirmation) {
        setSuccessMessage(result.message || 'Please check your email to confirm your account before signing in.');
        // Don't navigate - let user see the message and then go to login
      } else {
        // User is confirmed, navigate to home
      navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  return (
    <Box className="signup-page">
      {/* Logo at top left */}
      <Box className="signup-logo">
        <Box className="logo-icon">♠</Box>
        <Typography variant="h6" className="logo-text">
          Home Game
        </Typography>
      </Box>

      {/* Centered signup form */}
      <Box className="signup-container">
        <form onSubmit={handleSubmit} className="signup-form">
          <Typography variant="h4" className="signup-title">
            Sign up
          </Typography>

          <Typography variant="body1" className="signin-prompt">
            Already have an account?{' '}
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleSignIn();
              }}
              className="signin-link"
            >
              Sign in
            </Link>
          </Typography>

              {error && (
                <Alert severity="error" className="error-alert" onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

          {successMessage && (
            <Alert severity="info" className="success-alert" onClose={() => setSuccessMessage('')}>
              {successMessage}
            </Alert>
          )}

              <TextField
                fullWidth
            type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="signup-field"
            placeholder="Full name"
                disabled={isSubmitting || isLoading}
            autoComplete="name"
              />

              <TextField
                fullWidth
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="signup-field"
            placeholder="Email"
                disabled={isSubmitting || isLoading}
            autoComplete="email"
              />

              <TextField
                fullWidth
            type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                className="signup-field"
            placeholder="Password"
                disabled={isSubmitting || isLoading}
            autoComplete="new-password"
                helperText="Must be at least 6 characters"
              />

              <TextField
                fullWidth
            type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="signup-field"
            placeholder="Confirm password"
                disabled={isSubmitting || isLoading}
            autoComplete="new-password"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                className="signup-button"
                disabled={isSubmitting || isLoading || !formData.name || !formData.email || !formData.password || !formData.confirmPassword}
                size="large"
              >
            {isSubmitting ? 'Creating Account...' : 'Continue'}
              </Button>
            </form>
      </Box>
    </Box>
  );
};

export default SignUp;

